/* eslint-disable react/prop-types */
import * as React from 'react';

import { useUpdateEffect } from 'ahooks';
import { Button, Dropdown, Flex, Image, Space } from 'antd';

import {
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import type { PaymentGatewaySummary } from 'types';
import {
  adjustPageNumber,
  generateDownloadFile,
  generateTableDataSource,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    PAYMENT_GATEWAYS: { UPDATE_URL: PAYMENT_GATEWAYS_UPDATE_URL },
  },
} = PATHNAME;

type PaymentGatewaysTableProps = {
  loading: boolean;
  total: number;
  paymentGateways: PaymentGatewaySummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
};

const PaymentGatewaysTable = React.memo<PaymentGatewaysTableProps>(
  ({
    loading,
    total,
    paymentGateways,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
  }) => {
    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const paymentGatewaysDataSource = generateTableDataSource(
      paymentGateways,
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 500, y: 700 }}
        columns={[
          {
            title: 'عنوان',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (_, { id, title, iconId }) => (
              <Space>
                <Image
                  src={
                    iconId ? generateDownloadFile('Config', iconId) : undefined
                  }
                  fallback='http://placehold.jp/d9d9d9/200x200.png'
                  preview={!!iconId}
                  width={40}
                  height={40}
                  alt={title}
                />
                <AntdRouterLink
                  to={`${PAYMENT_GATEWAYS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {title}
                </AntdRouterLink>
              </Space>
            ),
          },
          {
            title: 'وضعیت فعال‌سازی',
            dataIndex: 'enable',
            key: 'enable',
            width: 150,
            ellipsis: true,
            render: (_, { enable }) => (
              <Flex justify='center'>
                {enable ? (
                  <CheckCircleOutlinedIcon className='text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-red-500' />
                )}
              </Flex>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-payment-gateway',
                          label: (
                            <AntdRouterLink
                              to={`${PAYMENT_GATEWAYS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش درگاه پرداخت
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={paymentGatewaysDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
PaymentGatewaysTable.displayName = 'PaymentGatewaysTable';

export { PaymentGatewaysTable };
